import React, {useEffect, useState} from 'react';
import axios from "axios";
import {toast} from "react-toastify";
import { DragDropContext } from 'react-beautiful-dnd';
import Column from "./Column";
import {CARD_CLICK_EVENT} from "./Events/CardClick";
import ShowProductModal from "../../../../modals/ShowProductModal";

/**
 * Structure to match COLUMN_DEFAULT
 * @type {[{label: string, value: string},{label: string, value: string},{label: string, value: string},{label: string, value: string}]}
 */
const COLUMNS = [{
    label:  'Pending',
    value:  'pending',
}, {
    label:  'Not Started',
    value:  'not_started',
}, {
    label:  'In Progress',
    value:  'in_progress',
}, {
    label:  'Complete',
    value:  'completed',
}];

const KanBan = (props) => {
    const {
        jobId       = null,
        orderId     = null,
        draggable   = false,
        parentId    = '',
        updateDetails,
    } = props;

    const [columns, setColumns]                     = useState([]);
    const [products, setProducts]                   = useState([]);
    const [showProductModal, setShowProductModal]   = useState(null);

    useEffect(() => {
        window.addEventListener(CARD_CLICK_EVENT, handleShowProductModal);

        return () => {
            window.removeEventListener(CARD_CLICK_EVENT, handleShowProductModal);
        }
    }, []);

    const handleShowProductModal = (event) => {
        setShowProductModal(event.detail.productId);
    }

    useEffect(() => {
        load();
    }, [jobId, orderId]);

    const load = () => {
        axios.get(route('web-api.job.order.product.index', {job: jobId, order: orderId}).toString())
            .then((response) => {
                if (!!response && !!response.data) {
                    if (!!response.data.products) {
                        setProducts(response.data.products);
                        setColumns(processColumns(response.data.products, response.data.column_orders));
                    }
                }
            })
            .catch((error) => {
                console.log(error);
                toast.error(<div>{"An error occurred"}</div>);
            });
    }

    const processColumns = (products, columnOrders) => {
        let columns = [];

        for (let i = 0; i < COLUMNS.length; i++) {
            const column = COLUMNS[i];

            columns.push({
                label:  column.label,
                value:  column.value,
                cards:  filterColumnProducts(column, products, columnOrders[column.value]),
            });
        }

        return columns;
    }

    const notDraggableError = () => {
        toast.error(<div>{"Unable to move products until due date and user are assigned to the order"}</div>);
    }

    const filterColumnProducts = (column, products, columnOrder) => {
        if (!products || (!!products && products.length <= 0)) {
            return [];
        }

        let processed = products.filter((product) => {
            return column.value === product.status;
        }).map((product) => {
            /**
             * Structure to match CARD_DEFAULT
             */
            return {
                id:             product.id,
                name:           product.reference+' - '+product.name,
                description:    "Qty: "+product.quantity,
            }
        });

        const reorderedProducts = [];
        for (let orderLoop = 0; orderLoop < columnOrder.length; orderLoop++) {
            let productId = columnOrder[orderLoop];

            for (let productLoop = 0; productLoop < processed.length; productLoop++) {
                let product = processed[productLoop];
                if (product.id === productId) {
                    reorderedProducts.push(product);
                    break;
                }
            }
        }

        return reorderedProducts;
    }

    const reorder = (list, startIndex, endIndex) => {
        const result = Array.from(list);
        const [removed] = result.splice(startIndex, 1);
        result.splice(endIndex, 0, removed);

        return result;
    };

    const onDragEnd = (result) => {
        if ((result.destination === null) ||
            ((result.destination.droppableId === result.source.droppableId) && (result.destination.index === result.source.index))
        ) {
            // No Destination -> Do Nothing
            return null;
        }

        const movedProduct = products.filter((product) => {
            return product.id === parseInt(result.draggableId);
        })[0];

        const source            = result.source;
        const destination       = result.destination;
        const processedColumns  = columns;

        /**
         * Check if the product changed column
         */
        if (source.droppableId === destination.droppableId) {
            /**
             * Product is in the same column:
             *
             * Reorder the column based on the new index.
             */
            if (draggable) {
                for (let columnIndex = 0; columnIndex < processedColumns.length; columnIndex++) {
                    if (processedColumns[columnIndex].value === destination.droppableId) {
                        processedColumns[columnIndex].cards = reorder(
                            processedColumns[columnIndex].cards,
                            result.source.index,
                            result.destination.index,
                        );
                        break;
                    }
                }
            } else {
                notDraggableError();
            }
        } else {
            /**
             * Product column has changed:
             *
             * Remove from the old column and reorder.
             *
             * Add to the new column and reorder the column based on the new index.
             */
            if (draggable) {
                for (let columnIndex = 0; columnIndex < processedColumns.length; columnIndex++) {
                    if (processedColumns[columnIndex].value === source.droppableId) {
                        /**
                         * This is the original column.
                         *
                         * Remove from the old column and reorder.
                         */
                        processedColumns[columnIndex].cards.splice(source.index, 1)

                    } else if (processedColumns[columnIndex].value === destination.droppableId) {
                        /**
                         * This is the new column.
                         *
                         * Add to the new column and reorder the column based on the new index.
                         */
                        processedColumns[columnIndex].cards.splice(destination.index, 0, {
                            id: movedProduct.id,
                            name: movedProduct.reference + ' - ' + movedProduct.name,
                            description: "Qty: " + movedProduct.quantity,
                        });
                    }
                }
            } else {
                notDraggableError();
            }
        }

        updateServer(processedColumns);

        setColumns(processedColumns);
    }

    const updateServer = (newColumns) => {
        axios.patch(route('web-api.job.order.product.reorder', {job: jobId, order: orderId}).toString(), {
            columns: newColumns
        }).then((response) => {
            // Reload the data
            load();
            updateDetails();
        }).catch((error) => {
            console.log(error);
            // Reload the data
            load();
        });
    }

    return (
        <React.Fragment>
            <div className={"kanban-container"}>
                <div className={"kanban-board"}>
                    <DragDropContext onDragEnd={(data) => onDragEnd(data)}>
                        {!!columns && columns.length > 0 && columns.map((column, index) => {
                            return (
                                <Column
                                    key={column.value}
                                    column={column}
                                />
                            )
                        })}
                    </DragDropContext>
                </div>
            </div>
            <ShowProductModal
                show={!!showProductModal}
                parentId={parentId}
                onCancel={() => setShowProductModal(null)}
                productId={!!showProductModal ? showProductModal : null}
            />
        </React.Fragment>
    )
}

export default KanBan;
